import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PatientManagement from './pages/receptionist/PatientManagement';
import AppointmentsPage from './pages/receptionist/AppointmentsPage';
import OPDPage from './pages/doctor/OPDPage';
import IPDPage from './pages/doctor/IPDPage';
import DischargeSummaryPage from './pages/doctor/DischargeSummaryPage';
import PatientDetailPage from './pages/doctor/PatientDetailPage';
import PrescriptionPrintPage from './pages/doctor/PrescriptionPrintPage';
import DoctorManagement from './pages/admin/DoctorManagement';
import StaffManagement from './pages/admin/StaffManagement';
import PrintSettings from './pages/admin/PrintSettings';
import MedicineManagement from './pages/admin/MedicineManagement';
import MediaManagement from './pages/admin/MediaManagement';
import InvestigationManagement from './pages/admin/LabTestManagement';
import ReferDoctorManagement from './pages/admin/ReferDoctorManagement';
import ReportsPage from './pages/reports/ReportsPage';
import MedicalLeaveCertificate from './pages/reports/MedicalLeaveCertificate';
import FitnessCertificateGeneral from './pages/reports/FitnessCertificateGeneral';
import FitnessCertificateJob from './pages/reports/FitnessCertificateJob';
import PregnancyCertificate from './pages/reports/PregnancyCertificate';
import MedicalLeaveCertificateForm3 from './pages/reports/MedicalLeaveCertificateForm3';
import FitnessCertificateForm4 from './pages/reports/FitnessCertificateForm4';
import MedicalCertificateForm5 from './pages/reports/MedicalCertificateForm5';
import ServiceManagement from './pages/admin/ServiceManagement';
import HospitalAnalyticsPage from './pages/admin/analytics/HospitalAnalyticsPage';
import RevenueDashboard from './pages/admin/analytics/RevenueDashboard';
import DoctorPerformanceDashboard from './pages/admin/analytics/DoctorPerformanceDashboard';
import EDDCalendarPage from './pages/EDDCalendarPage';
import BillingPage from './pages/billing/BillingPage';
import AttendancePage from './pages/admin/AttendancePage';
import LeaveManagementPage from './pages/admin/LeaveManagementPage';
import MyLeavePage from './pages/MyLeavePage';
import SalaryStructurePage from './pages/admin/SalaryStructurePage';
import SalaryProcessingPage from './pages/admin/SalaryProcessingPage';
import ExpensesPage from './pages/admin/ExpensesPage';
import PayrollExpenseDashboard from './pages/admin/analytics/PayrollExpenseDashboard';

// Components
import ProtectedRoute from './components/ProtectedRoute';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
    },
    secondary: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ffc107',
    },
    info: {
      main: '#2196f3',
    },
    success: {
      main: '#4caf50',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 700,
    },
    h2: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 700,
    },
    h3: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          
          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          
          {/* Receptionist Routes */}
          <Route
            path="/patient-management"
            element={
              <ProtectedRoute allowedRoles={['Receptionist', 'Doctor', 'Admin']}>
                <PatientManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <ProtectedRoute allowedRoles={['Receptionist', 'Doctor', 'Admin']}>
                <AppointmentsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Doctor Routes */}
          <Route
            path="/opd"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <OPDPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ipd"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <IPDPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient/:id/:appointmentId?"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <PatientDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ipd/discharge/:appointmentId"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <DischargeSummaryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prescription/:id/print"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Receptionist', 'Admin']}>
                <PrescriptionPrintPage />
              </ProtectedRoute>
            }
          />
          
          {/* OBG EDD Calendar */}
          <Route
            path="/obg-edd-calendar"
            element={
              <ProtectedRoute allowedRoles={['Receptionist', 'Admin', 'Doctor']}>
                <EDDCalendarPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/doctors"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <DoctorManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/staff"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <StaffManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/print-settings"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <PrintSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/medicines"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <MedicineManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/media"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <MediaManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/investigations"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <InvestigationManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/refer-doctors"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <ReferDoctorManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/medical-leave"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <MedicalLeaveCertificate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/fitness-general"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <FitnessCertificateGeneral />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/fitness-job"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <FitnessCertificateJob />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/pregnancy"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <PregnancyCertificate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/medical-leave-form3"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <MedicalLeaveCertificateForm3 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/fitness-form4"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <FitnessCertificateForm4 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/medical-form5"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Admin']}>
                <MedicalCertificateForm5 />
              </ProtectedRoute>
            }
          />

          {/* Billing Route */}
          <Route
            path="/billing"
            element={
              <ProtectedRoute allowedRoles={['Receptionist', 'Doctor', 'Admin']}>
                <BillingPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/services"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <ServiceManagement />
              </ProtectedRoute>
            }
          />

          {/* Analytics Routes (Admin only) */}
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <HospitalAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics/revenue"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <RevenueDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics/doctor-performance"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <DoctorPerformanceDashboard />
              </ProtectedRoute>
            }
          />

          {/* Expenses & Payroll Dashboard Routes (Admin only) */}
          <Route
            path="/admin/expenses"
            element={
              <ProtectedRoute allowedRoles={['Admin', 'Manager']}>
                <ExpensesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics/payroll-expense"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <PayrollExpenseDashboard />
              </ProtectedRoute>
            }
          />

          {/* Staff Attendance & Salary Routes (Admin only) */}
          <Route
            path="/admin/attendance"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <AttendancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/leaves"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <LeaveManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-leaves"
            element={
              <ProtectedRoute allowedRoles={['Doctor', 'Receptionist', 'LabAttendant', 'Manager']}>
                <MyLeavePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/salary-structure"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <SalaryStructurePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/salary-processing"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <SalaryProcessingPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
